import React, { lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import GoogleMapsProvider from './contexts/GoogleMapsContext';
import ErrorBoundary from './components/ErrorBoundary';
import RoleBasedRoute from './components/layout/RoleBasedRoute';
import { SUPER_ADMIN_PATH } from './config/secret';

/**
 * Root redirect — sends visitors from "/" to the appropriate page
 * based on their authentication state.
 */
const RootRedirect: React.FC = () => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === 'driver' ? '/driver-panel' : '/dashboard'} replace />;
};

const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Orders = lazy(() => import('./pages/Orders'));
const CreateOrder = lazy(() => import('./pages/CreateOrder'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const Drivers = lazy(() => import('./pages/Drivers'));
const CreateDriver = lazy(() => import('./pages/CreateDriver'));
const DriverDetail = lazy(() => import('./pages/DriverDetail'));
const DriverEarnings = lazy(() => import('./pages/DriverEarnings'));
const Team = lazy(() => import('./pages/Team'));
const CreateTeamMember = lazy(() => import('./pages/CreateTeamMember'));
const EditTeamMember = lazy(() => import('./pages/EditTeamMember'));
const PublicTracking = lazy(() => import('./pages/PublicTracking'));
const LiveTracking = lazy(() => import('./pages/LiveTracking'));
const DriverPanel = lazy(() => import('./pages/DriverPanel'));
const BusinessSettings = lazy(() => import('./pages/BusinessSettings'));
const Branches = lazy(() => import('./pages/Branches'));
const CreateBranch = lazy(() => import('./pages/CreateBranch'));
const BranchDetail = lazy(() => import('./pages/BranchDetail'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));
const RatingPage = lazy(() => import('./pages/RatingPage'));
const NotFound = lazy(() => import('./pages/NotFound'));
const NotificationHistory = lazy(() => import('./pages/NotificationHistory'));
const CODReconciliation = lazy(() => import('./pages/CODReconciliation'));
const OrderEdit = lazy(() => import('./pages/OrderEdit'));
const DispatcherBoard = lazy(() => import('./pages/DispatcherBoard'));
const PrintManifest = lazy(() => import('./pages/PrintManifest'));
const DeliveryPricing = lazy(() => import('./pages/DeliveryPricing'));
const DeliveryPartnerships = lazy(() => import('./pages/DeliveryPartnerships'));
const AgencyMode = lazy(() => import('./pages/AgencyMode'));

// ── Super Admin ──────────────────────────────────────────────
const SuperAdminLogin = lazy(() => import('./pages/SuperAdminLogin'));
const SALayout = lazy(() => import('./pages/superadmin/layout/SALayout'));
const SADashboard = lazy(() => import('./pages/superadmin/dashboard/SADashboard'));
const SABusinesses = lazy(() => import('./pages/superadmin/businesses/SABusinesses'));
const SABusinessDetail = lazy(() => import('./pages/superadmin/businesses/SABusinessDetail'));
const SAUsers = lazy(() => import('./pages/superadmin/users/SAUsers'));
const SADrivers = lazy(() => import('./pages/superadmin/drivers/SADrivers'));
const SAOrders = lazy(() => import('./pages/superadmin/orders/SAOrders'));
const SARevenue = lazy(() => import('./pages/superadmin/finance/SARevenue'));
const SAPlans = lazy(() => import('./pages/superadmin/finance/SAPlans'));
const SATransactions = lazy(() => import('./pages/superadmin/finance/SATransactions'));
const SASystemStatus = lazy(() => import('./pages/superadmin/platform/SASystemStatus'));
const SAWhatsappUsage = lazy(() => import('./pages/superadmin/platform/SAWhatsappUsage'));
const SAMapUsage = lazy(() => import('./pages/superadmin/platform/SAMapUsage'));
const SAFeatureFlags = lazy(() => import('./pages/superadmin/platform/SAFeatureFlags'));
const SABroadcast = lazy(() => import('./pages/superadmin/communications/SABroadcast'));
const SAMessageHistory = lazy(() => import('./pages/superadmin/communications/SAMessageHistory'));
const SAAdminAccounts = lazy(() => import('./pages/superadmin/system/SAAdminAccounts'));
const SAAuditLog = lazy(() => import('./pages/superadmin/system/SAAuditLog'));
const SAApiKeys = lazy(() => import('./pages/superadmin/system/SAApiKeys'));
const SASettings = lazy(() => import('./pages/superadmin/system/SASettings'));

const PageLoader = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg, #f5faf8)',
    gap: 24,
  }}>
    <style>{`
      @keyframes lt-spin { to { transform: rotate(360deg) } }
      @keyframes lt-pulse { 0%,100% { opacity: .3 } 50% { opacity: 1 } }
      @keyframes lt-fadeIn { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }
    `}</style>
    <div style={{ animation: 'lt-fadeIn .4s ease-out both', textAlign: 'center' }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        border: '3px solid var(--border, #daeee7)',
        borderTopColor: 'var(--green-500, #1e9068)',
        animation: 'lt-spin .8s linear infinite',
        margin: '0 auto 20px',
      }} />
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 22 }}>📦</span>
        <span style={{
          fontFamily: "'Poppins','Montserrat',sans-serif",
          fontWeight: 800, fontSize: 18,
          background: 'linear-gradient(135deg, #1a7a5a, #1e9068)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          letterSpacing: '-.02em',
        }}>LiveTrack</span>
      </div>
      <p style={{
        marginTop: 10, fontSize: 13,
        color: 'var(--ink-40, #7a9990)',
        fontFamily: "'Montserrat',sans-serif",
        animation: 'lt-pulse 1.5s ease-in-out infinite',
      }}>Loading your workspace…</p>
    </div>
  </div>
);


function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <GoogleMapsProvider>
          <AuthProvider>
            <ToastProvider>
              <Router>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Root redirect — sends to /login or /dashboard based on auth */}
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/register" element={<Register />} />
                    <Route
                      path="/onboarding"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner']}>
                          <Onboarding />
                        </RoleBasedRoute>
                      }
                    />
                    <Route path="/track/:trackingId" element={<PublicTracking />} />
                    <Route path="/feedback/:orderId" element={<RatingPage />} />
                    <Route path="/unauthorized" element={<Unauthorized />} />

                    {/* ── Super Admin — login (no layout) ── */}
                    <Route path={`/${SUPER_ADMIN_PATH}`} element={<SuperAdminLogin />} />
                    <Route path={`/${SUPER_ADMIN_PATH}/login`} element={<SuperAdminLogin />} />

                    {/* ── Super Admin — all protected pages share SALayout ── */}
                    <Route path={`/${SUPER_ADMIN_PATH}`} element={<SALayout />}>
                      <Route path="dashboard" element={<SADashboard />} />
                      <Route path="businesses" element={<SABusinesses />} />
                      <Route path="businesses/:id" element={<SABusinessDetail />} />
                      <Route path="users" element={<SAUsers />} />
                      <Route path="drivers" element={<SADrivers />} />
                      <Route path="orders" element={<SAOrders />} />
                      <Route path="finance/revenue" element={<SARevenue />} />
                      <Route path="finance/plans" element={<SAPlans />} />
                      <Route path="finance/transactions" element={<SATransactions />} />
                      <Route path="platform/status" element={<SASystemStatus />} />
                      <Route path="platform/whatsapp" element={<SAWhatsappUsage />} />
                      <Route path="platform/maps" element={<SAMapUsage />} />
                      <Route path="platform/flags" element={<SAFeatureFlags />} />
                      <Route path="communications/broadcast" element={<SABroadcast />} />
                      <Route path="communications/history" element={<SAMessageHistory />} />
                      <Route path="system/admins" element={<SAAdminAccounts />} />
                      <Route path="system/audit" element={<SAAuditLog />} />
                      <Route path="system/api-keys" element={<SAApiKeys />} />
                      <Route path="settings" element={<SASettings />} />
                    </Route>

                    <Route
                      path="/dashboard"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <Dashboard />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/analytics"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <Analytics />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/orders"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <Orders />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/orders/new"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <CreateOrder />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/orders/:id"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <OrderDetail />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/drivers"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <Drivers />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/drivers/new"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <CreateDriver />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/drivers/:id"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <DriverDetail />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/drivers/:id/earnings"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <DriverEarnings />
                        </RoleBasedRoute>
                      }
                    />
                    {/* Team: business_owner only */}
                    <Route
                      path="/team"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner']} redirectTo="/dashboard">
                          <Team />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/team/new"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner']} redirectTo="/dashboard">
                          <CreateTeamMember />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/team/:id/edit"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner']} redirectTo="/dashboard">
                          <EditTeamMember />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/tracking"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <LiveTracking />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/driver-panel"
                      element={
                        <RoleBasedRoute allowedRoles={['driver']} redirectTo="/dashboard">
                          <DriverPanel />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <BusinessSettings />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/manifest"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <PrintManifest />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/branches"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <Branches />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/branches/new"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <CreateBranch />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/branches/:id"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <BranchDetail />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/notifications"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <NotificationHistory />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/cod-reconciliation"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <CODReconciliation />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/orders/:id/edit"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <OrderEdit />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/dispatcher"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <DispatcherBoard />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/delivery-pricing"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <DeliveryPricing />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/partnerships"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <DeliveryPartnerships />
                        </RoleBasedRoute>
                      }
                    />
                    <Route
                      path="/agency"
                      element={
                        <RoleBasedRoute allowedRoles={['business_owner', 'admin']} redirectTo="/driver-panel">
                          <AgencyMode />
                        </RoleBasedRoute>
                      }
                    />
                    {/* Fallbacks */}
                    <Route path="/unauthorized" element={<Unauthorized />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </Router>
            </ToastProvider>
          </AuthProvider>
        </GoogleMapsProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
